<template>
  <div class="page-container">
    <div class="page-header">
      <h2 class="page-title">AI 写小说 · Studio（流水线版）</h2>
      <p class="page-subtitle">保存/创建 → 初始化章节 → 一键启动流水线（灵感→大纲→全章节）</p>
    </div>

    <div class="studio-layout">
      <!-- 左侧：创作条件 + 操作 -->
      <div class="left-pane">
        <el-card shadow="never" class="card">
          <div class="card-header">
            <div class="icon-wrapper"><el-icon>
                <Edit />
              </el-icon></div>
            <div>
              <h3 class="auth-title">创作条件</h3>
              <p class="tip-text">提示：<b>条件越少，生成效果越好</b>；也可直接使用随机条件</p>
            </div>
          </div>

          <el-form :model="form" label-width="100px" class="elegant-form">
            <el-form-item label="小说标题">
              <el-input v-model="form.title" placeholder="留空则生成时自动命名" />
            </el-form-item>

            <el-form-item label="封面">
              <el-upload class="avatar-uploader" :show-file-list="false" :before-upload="beforeCoverUpload"
                :http-request="handleCoverUpload" :disabled="uploadingCover">
                <img v-if="form.cover_url" :src="form.cover_url" class="cover" />
                <div v-else class="empty-cover">
                  <el-icon class="plus-icon">
                    <Plus v-if="!uploadingCover" />
                    <Loading v-else class="loading-icon" />
                  </el-icon>
                </div>
              </el-upload>
            </el-form-item>

            <!-- 随机条件开关 + 随机一组 + 预览 -->
            <el-form-item label="随机条件">
              <div class="rand-row">
                <el-switch v-model="form.random_mode" :active-value="1" :inactive-value="0" @change="onRandomToggle" />
                <el-button size="small" :disabled="!form.random_mode" @click="randomize">
                  <el-icon>
                    <MagicStick />
                  </el-icon> 随机一组
                </el-button>
                <el-checkbox v-model="randomFull" :disabled="!form.random_mode"
                  title="勾选后会尽量为更多字段随机赋值">全量随机</el-checkbox>
              </div>
            </el-form-item>

            <!-- 随机预览 -->
            <el-form-item v-if="form.random_mode" label="随机结果">
              <div class="tags-row">
                <el-tag>{{ form.type || '（未设）' }}</el-tag>
                <el-tag>{{ form.language || '（未设）' }}</el-tag>
                <el-tag>{{ form.platform || '（未设）' }}</el-tag>
                <el-tag>{{ form.length_type || '（未设）' }}</el-tag>
                <el-tag>{{ form.pov || '（未设）' }}</el-tag>
                <el-tag>{{ form.style_mode || '（未设）' }}</el-tag>
                <el-tag>{{ form.era || '（未设）' }}</el-tag>
                <el-tag>{{ form.genre || '（未设）' }}</el-tag>
                <el-tag>{{ form.style || '（未设）' }}</el-tag>
                <el-tag>金手指：{{ form.golden_finger === 1 ? (form.golden_finger_type || '是') : '否' }}</el-tag>
                <template v-if="randomFull">
                  <el-tag>{{ form.title_pattern || '（未设）' }}</el-tag>
                  <el-tag>{{ form.female_role || '（未设）' }}</el-tag>
                  <el-tag>{{ form.male_role || '（未设）' }}</el-tag>
                  <el-tag>{{ form.villain_role || '（未设）' }}</el-tag>
                  <el-tag>{{ form.male_job || '（未设）' }}</el-tag>
                  <el-tag>{{ form.female_job || '（未设）' }}</el-tag>
                  <el-tag>{{ form.plot || '（未设）' }}</el-tag>
                  <el-tag>{{ form.world || '（未设）' }}</el-tag>
                </template>
              </div>
            </el-form-item>

            <!-- 手动选择（关闭随机时显示） -->
            <el-row :gutter="12" v-show="!form.random_mode">
              <el-col :span="12">
                <el-form-item label="类型">
                  <el-select v-model="form.type" filterable allow-create>
                    <el-option v-for="v in types" :key="v" :label="v" :value="v" />
                  </el-select>

                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="语言">
                  <el-select v-model="form.language" filterable allow-create>
                    <el-option v-for="v in languages" :key="v" :label="v" :value="v" />
                  </el-select>
                </el-form-item>
              </el-col>

              <el-col :span="12">
                <el-form-item label="平台">
                  <el-select v-model="form.platform" filterable allow-create>
                    <el-option v-for="p in platforms" :key="p" :label="p" :value="p" />
                  </el-select>
                </el-form-item>
              </el-col>

              <el-col :span="12">
                <el-form-item label="篇幅">
                  <el-select v-model="form.length_type" filterable allow-create>
                    <el-option v-for="v in lengthTypes" :key="v" :label="v" :value="v" />
                  </el-select>
                </el-form-item>
              </el-col>

              <el-col :span="12">
                <el-form-item label="视角">
                  <el-select v-model="form.pov" filterable allow-create>
                    <el-option v-for="v in povs" :key="v" :label="v" :value="v" />
                  </el-select>
                </el-form-item>
              </el-col>

              <el-col :span="12">
                <el-form-item label="文风">
                  <el-select v-model="form.style_mode" filterable allow-create>
                    <el-option v-for="v in styleModes" :key="v" :label="v" :value="v" />
                  </el-select>
                </el-form-item>
              </el-col>

              <el-col :span="12">
                <el-form-item label="年代">
                  <el-select v-model="form.era" filterable allow-create>
                    <el-option v-for="v in eras" :key="v" :label="v" :value="v" />
                  </el-select>
                </el-form-item>
              </el-col>

              <el-col :span="12">
                <el-form-item label="题材">
                  <el-select v-model="form.genre" filterable allow-create>
                    <el-option v-for="g in genres" :key="g" :label="g" :value="g" />
                  </el-select>
                </el-form-item>
              </el-col>

              <el-col :span="12">
                <el-form-item label="金手指">
                  <el-select v-model="form.golden_finger" filterable allow-create>
                    <el-option v-for="opt in goldenFingerOptions" :key="opt.value" :label="opt.label"
                      :value="opt.value" />
                  </el-select>
                </el-form-item>
              </el-col>

              <el-col :span="12" v-if="form.golden_finger === 1">
                <el-form-item label="类型">
                  <el-select v-model="form.golden_finger_type" filterable allow-create>
                    <el-option v-for="t in fingerTypes" :key="t" :label="t" :value="t" />
                  </el-select>
                </el-form-item>
              </el-col>

              <el-col :span="12">
                <el-form-item label="标题结构">
                  <el-select v-model="form.title_pattern" filterable allow-create>
                    <el-option v-for="t in titlePatterns" :key="t" :label="t" :value="t" />
                  </el-select>
                </el-form-item>
              </el-col>

              <el-col :span="12">
                <el-form-item label="风格">
                  <el-select v-model="form.style" filterable allow-create>
                    <el-option v-for="s in styles" :key="s" :label="s" :value="s" />
                  </el-select>
                </el-form-item>
              </el-col>

              <el-col :span="12">
                <el-form-item label="女主人设">
                  <el-select v-model="form.female_role" filterable allow-create>
                    <el-option v-for="i in femaleRoles" :key="i" :label="i" :value="i" />
                  </el-select>
                </el-form-item>
              </el-col>

              <el-col :span="12">
                <el-form-item label="男主人设">
                  <el-select v-model="form.male_role" filterable allow-create>
                    <el-option v-for="i in maleRoles" :key="i" :label="i" :value="i" />
                  </el-select>
                </el-form-item>
              </el-col>

              <el-col :span="12">
                <el-form-item label="反派人设">
                  <el-select v-model="form.villain_role" filterable allow-create>
                    <el-option v-for="i in villainRoles" :key="i" :label="i" :value="i" />
                  </el-select>
                </el-form-item>
              </el-col>

              <el-col :span="12">
                <el-form-item label="男主职业">
                  <el-select v-model="form.male_job" filterable allow-create>
                    <el-option v-for="i in maleJobs" :key="i" :label="i" :value="i" />
                  </el-select>
                </el-form-item>
              </el-col>

              <el-col :span="12">
                <el-form-item label="女主职业">
                  <el-select v-model="form.female_job" filterable allow-create>
                    <el-option v-for="i in femaleJobs" :key="i" :label="i" :value="i" />
                  </el-select>
                </el-form-item>
              </el-col>

              <el-col :span="12">
                <el-form-item label="情节">
                  <el-select v-model="form.plot" filterable allow-create>
                    <el-option v-for="i in plots" :key="i" :label="i" :value="i" />
                  </el-select>
                </el-form-item>
              </el-col>

              <el-col :span="12">
                <el-form-item label="时空背景">
                  <el-select v-model="form.world" filterable allow-create>
                    <el-option v-for="i in worlds" :key="i" :label="i" :value="i" />
                  </el-select>
                </el-form-item>
              </el-col>

              <el-col :span="24">
                <el-form-item label="背景补充">
                  <el-input v-model="form.background" placeholder="其它背景（可空）" />
                </el-form-item>
              </el-col>
            </el-row>

            <!-- ✅ 新增 10 章选项 -->
            <el-form-item label="章节大纲">
              <el-radio-group v-model="form.chapter_plan">
                <el-radio :label="10">约10章（试跑）</el-radio>
                <el-radio :label="100">约100章</el-radio>
                <el-radio :label="400">约400章</el-radio>
                <el-radio :label="550">约550章</el-radio>
              </el-radio-group>
              <el-input-number v-model="form.chapter_plan" :min="1" :max="2000" placeholder="输入章节数量"
                controls-position="right" class="uniform-width" />
            </el-form-item>

            <el-alert type="warning" :closable="false" show-icon title="提示：务必保证字数充足，否则会写不完整！" class="mb-12" />

            <div class="btn-row">
              <el-button type="primary" @click="createNovel" :loading="savingNovel">保存/创建小说</el-button>
              <el-button @click="initChapters" :disabled="!novelId">初始化章节架构</el-button>
            </div>

            <!-- 当前流程状态提示（来自轮询） -->
            <div class="mt-8">
              <el-tag :type="chaptersInited ? 'success' : 'info'" class="mr-8">
                章节架构：{{ chaptersInited ? '已初始化' : '未初始化' }}
              </el-tag>
              <el-tag :type="pipeStatus.inspiration_done ? 'success' : 'info'" class="mr-8">
                灵感：{{ pipeStatus.inspiration_done ? '已完成' : '等待' }}
              </el-tag>
              <el-tag :type="pipeStatus.outline_done ? 'success' : 'info'">
                大纲：{{ pipeStatus.outline_done ? '已完成' : '等待' }}
              </el-tag>
            </div>
          </el-form>
        </el-card>

        <!-- 生成流水线 -->
        <el-card shadow="never" class="card">
          <div class="card-header">
            <div class="icon-wrapper"><el-icon>
                <Document />
              </el-icon></div>
            <div>
              <h3 class="auth-title">生成流水线</h3>
              <p class="tip-text">自动完成：灵感 → 大纲 → 全章节</p>
            </div>
          </div>

          <div class="btn-col">
            <el-button type="primary" :disabled="!novelId || !chaptersInited" @click="startPipeline">
              启动自动生成
            </el-button>
            <el-alert v-if="!novelId" type="info" :closable="false" title="请先保存/创建小说" class="mb-12" />
            <el-alert v-else-if="!chaptersInited" type="warning" :closable="false" title="请先初始化章节架构" class="mb-12" />

            <el-divider />
            <div>
              <div class="mb-12">
                <el-tag :type="pipeStatus.inspiration_done ? 'success' : 'info'">灵感：{{ pipeStatus.inspiration_done ?
                  '完成' : '等待'
                }}</el-tag>
                <el-tag class="ml-8" :type="pipeStatus.outline_done ? 'success' : 'info'">大纲：{{ pipeStatus.outline_done
                  ?
                  '完成' : '等待' }}</el-tag>
              </div>
              <el-progress :percentage="pipeStatus.percent" :text-inside="true" :stroke-width="20" />
              <p class="tip-text mt-8">
                章节进度：{{ pipeStatus.chapters_done }}/{{ pipeStatus.chapters_total }}
                <span v-if="pipeStatus.running">（当前：{{ labelRunning(pipeStatus.running) }}）</span>
              </p>
            </div>
          </div>
        </el-card>
      </div>

      <!-- 右侧：信息区 -->
      <div class="right-pane">
        <el-card shadow="never" class="card">
          <div class="card-header">
            <div class="icon-wrapper"><el-icon>
                <View />
              </el-icon></div>
            <div>
              <h3 class="auth-title">实时信息</h3>
              <p class="tip-text">流水线在后台运行。你可在“小说列表/详情”里查看章节与发起重写/续写。</p>
            </div>
          </div>

          <div class="stream-box">
            <pre class="stream-text">
        1. 先“保存/创建小说”，再“初始化章节架构”
        2. 点击“启动自动生成”，后台自动跑：灵感→大纲→全章节
        3. 不满意章节可在详情页发起“重写/续写”（会入队任务，不阻塞其它生成）
      </pre>
          </div>
        </el-card>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import axios from 'axios'
import Cookies from 'js-cookie'
import config from '@/config'
import { Edit, Plus, Loading, Document, View, MagicStick } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import { getUploadSignature, uploadToOSS, beforeUpload } from '@/utils/uploadUtils'
import { useNovelMeta } from '@/composables/useNovelMeta'
import { useRoute } from 'vue-router'

const route = useRoute()
const userInfo = JSON.parse(Cookies.get('user_info') || '{}')
const storeId = userInfo.id || 0

// ==== 枚举常量 ====
const {
  types, languages, platforms, lengthTypes, povs, styleModes, eras, genres,
  goldenFingerOptions, fingerTypes, titlePatterns, styles, femaleRoles, maleRoles,
  villainRoles, maleJobs, femaleJobs, plots, worlds, loadMeta
} = useNovelMeta()

// ==== 表单 ====
const form = reactive({
  title: '',
  cover_url: '',
  random_mode: 1,
  type: '女频',
  language: '中文',
  platform: '番茄',
  length_type: '中长篇',
  pov: '第三人称',
  style_mode: '强爽点',
  era: '古代',
  genre: '言情',
  golden_finger: 0,
  golden_finger_type: '',
  title_pattern: '【情绪事件】+【反差】',
  style: '虐恋文',
  female_role: '',
  male_role: '',
  villain_role: '',
  male_job: '',
  female_job: '',
  plot: '',
  world: '',
  background: '无',
  // ✅ 默认改为 10 章，先小规模试跑
  chapter_plan: 10,
})

// ==== 状态 ====
const randomFull = ref(false)
const uploadingCover = ref(false)
const novelId = ref(null)
const chaptersInited = ref(false)
const savingNovel = ref(false)

// ==== 随机化 ====
function pick(arr) { return arr[Math.floor(Math.random() * arr.length)] }
function maybe(arr, fieldName) {
  // 如果该字段已经有自定义值（不在预设选项中），保留原值
  if (form[fieldName] && !arr.includes(form[fieldName])) {
    return form[fieldName];
  }
  return Math.random() < 0.7 ? pick(arr) : '';
}

function randomize() {
  // 只有在随机模式下才覆盖值
  if (form.random_mode !== 1) return;

  // 基础信息随机
  form.type = pick(types.value);
  form.language = pick(languages.value);
  form.platform = pick(platforms.value);
  form.length_type = pick(lengthTypes.value);
  form.pov = pick(povs.value);
  form.style_mode = pick(styleModes.value);
  form.era = pick(eras.value);

  // 题材和风格过滤掉"随机"选项
  const gs = (genres.value || []).filter(g => g !== '随机');
  form.genre = pick(gs.length ? gs : genres.value);
  const ss = (styles.value || []).filter(s => s !== '随机');
  form.style = pick(ss.length ? ss : styles.value);

  // 金手指相关
  form.golden_finger = Math.random() < 0.35 ? 1 : 0;
  form.golden_finger_type = form.golden_finger ? pick(fingerTypes.value) : '';

  // 全量随机和部分随机模式
  if (randomFull.value) {
    // 全量随机 - 所有字段都赋值
    form.title_pattern = pick(titlePatterns.value);
    form.female_role = pick(femaleRoles.value);
    form.male_role = pick(maleRoles.value);
    form.villain_role = pick(villainRoles.value);
    form.male_job = pick(maleJobs.value);
    form.female_job = pick(femaleJobs.value);
    form.plot = pick(plots.value);
    form.world = pick(worlds.value);
  } else {
    // 部分随机 - 只随机部分主要字段
    form.title_pattern = pick(titlePatterns.value);
    form.female_role = maybe(femaleRoles.value, 'female_role');
    form.male_role = maybe(maleRoles.value, 'male_role');
    // 以下字段设为空，保留用户可能已输入的值
    form.villain_role = form.villain_role || '';
    form.male_job = form.male_job || '';
    form.female_job = form.female_job || '';
    form.plot = form.plot || '';
    form.world = form.world || '';
  }

  // 背景补充固定为"无"，但可以保留用户已修改的值
  if (!form.background || form.background === '无') {
    form.background = '无';
  }
}
function onRandomToggle(v) { if (v === 1) randomize() }

// ==== 上传封面 ====
async function beforeCoverUpload(file) { return await beforeUpload(file, { maxSize: 3 }) }
async function handleCoverUpload(options) {
  try {
    uploadingCover.value = true
    const sig = await getUploadSignature('image')
    const ret = await uploadToOSS(options.file, sig)
    form.cover_url = ret.url
    options.onSuccess({ data: { url: ret.url } })
  } catch (e) {
    options.onError(e)
  } finally {
    uploadingCover.value = false
  }
}

// ==== 创建小说 ====
async function createNovel() {
  try {
    savingNovel.value = true
    const payload = {
      store_id: storeId,
      title: form.title || '未命名长篇',
      cover_url: form.cover_url,
      random_mode: form.random_mode,
      type: form.type,
      language: form.language,
      platform: form.platform,
      length_type: form.length_type,
      pov: form.pov,
      style_mode: form.style_mode,
      era: form.era,
      genre: form.genre,
      golden_finger: form.golden_finger,
      golden_finger_type: form.golden_finger_type,
      title_pattern: form.title_pattern,
      style: form.style,
      female_role: form.female_role,
      male_role: form.male_role,
      villain_role: form.villain_role,
      male_job: form.male_job,
      female_job: form.female_job,
      plot: form.plot,
      world: form.world,
      background: form.background,
      // 把 10/100/400/550 带给后端
      chapter_plan: form.chapter_plan,
    }
    const { data } = await axios.post(`${config.BASE_URL}/shop/api/novel_api.php`, payload)
    if (data.code === 200) {
      novelId.value = data.data.id
      chaptersInited.value = false
      ElMessage.success('小说创建成功')
      pollStatus()
    } else {
      ElMessage.error(data.message || '创建失败')
    }
  } finally {
    savingNovel.value = false
  }
}

// ==== 初始化章节 ====
async function initChapters() {
  if (!novelId.value) { ElMessage.warning('请先创建小说'); return }
  const { data } = await axios.post(`${config.BASE_URL}/shop/api/novel_chapter_api.php`, {
    mode: 'bulk_init',
    store_id: storeId,
    novel_id: novelId.value
  })
  if (data.code === 200) {
    chaptersInited.value = true
    ElMessage.success(data.message || '初始化完成')
    pollStatus()
  } else {
    ElMessage.error(data.message || '初始化失败')
  }
}

// ==== 流水线 ====
const pipeStatus = reactive({
  inspiration_done: false,
  outline_done: false,
  chapters_total: 0,
  chapters_done: 0,
  percent: 0,
  running: null
})
let timer = null
function pollStatus() {
  if (!novelId.value) return
  axios.get(`${config.BASE_URL}/shop/api/novel_pipeline_api.php`, {
    params: { action: 'status', store_id: storeId, novel_id: novelId.value }
  }).then(({ data }) => {
    if (data.code === 200) {
      Object.assign(pipeStatus, data.data || {})
      // 同步“是否已初始化章节”
      chaptersInited.value = (pipeStatus.chapters_total || 0) > 0 || chaptersInited.value
    }
  }).catch(() => { })
}
function startPipeline() {
  if (!novelId.value) { ElMessage.warning('请先创建小说'); return }
  if (!chaptersInited.value) { ElMessage.warning('请先初始化章节架构'); return }
  axios.post(`${config.BASE_URL}/shop/api/novel_pipeline_api.php`, {
    action: 'start', store_id: storeId, novel_id: novelId.value
  }).then(({ data }) => {
    if (data.code === 200) {
      ElMessage.success('流水线已启动')
      if (timer) clearInterval(timer)
      timer = setInterval(pollStatus, 2000)
    } else {
      ElMessage.error(data.message || '启动失败')
    }
  }).catch(() => {
    ElMessage.error('启动失败')
  })
}
function labelRunning(r) {
  if (!r) return ''
  if (r.kind === 'chapter' || r.kind === 'continue' || r.kind === 'rewrite') {
    return `写章节#${r.chapter_id}（${r.progress || 0}%）`
  }
  return r.kind
}

// ==== 生命周期 ====
onMounted(async () => {
  await loadMeta()
  if (form.random_mode === 1) randomize()

  // 从路由接收 novel_id
  const qNovelId = Number(route.query.novel_id || 0)
  // 可选：从路由接收 chapter_plan（例如 ?chapter_plan=10）
  const qPlan = Number(route.query.chapter_plan || 0)
  if (qPlan > 0) form.chapter_plan = qPlan

  if (qNovelId) {
    novelId.value = qNovelId
    pollStatus()
    if (timer) clearInterval(timer)
    timer = setInterval(pollStatus, 3000)
  }
})
</script>

<style scoped lang="scss">
.page-container {
  background: linear-gradient(135deg, #f5f7fa, #e4e8ed);
  min-height: 100vh;
}

.page-header {
  padding: 24px 30px;
  background: #fff;
  margin-bottom: 16px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, .05);
}

.page-title {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
}

.page-subtitle {
  margin-top: 6px;
  color: #666;
}

.studio-layout {
  display: flex;
  gap: 16px;
  margin: 0 auto;
  padding: 0 16px;
}

.left-pane {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.right-pane {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.card {
  border-radius: 12px;
}

.card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;
  margin-bottom: 12px;
}

.icon-wrapper {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(64, 158, 255, .1);
  color: #409EFF;
}

.auth-title {
  margin: 0;
  font-weight: 600;
}

.tip-text {
  font-size: 12px;
  color: #888;
}

.elegant-form .el-form-item {
  margin-bottom: 14px;
}

.btn-row {
  display: flex;
  gap: 12px;
}

.stream-box {
  height: 360px;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 12px;
  background: #fff;
  overflow-y: auto;
}

.stream-text {
  white-space: pre-wrap;
  word-break: break-word;
  font-family: system-ui, -apple-system, "PingFang SC", "Microsoft Yahei", monospace;
  font-size: 14px;
  line-height: 1.75;
}

.avatar-uploader .empty-cover {
  width: 120px;
  height: 160px;
  border: 2px dashed var(--el-border-color);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cover {
  width: 120px;
  height: 160px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid var(--el-border-color);
}

.plus-icon {
  font-size: 28px;
  color: #999;
}

.loading-icon {
  animation: rotating 2s linear infinite;
  font-size: 26px;
}

@keyframes rotating {
  from {
    transform: rotate(0)
  }

  to {
    transform: rotate(360deg)
  }
}

.mb-12 {
  margin-bottom: 12px;
}

.mr-8 {
  margin-right: 8px;
}

.btn-col {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.rand-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.tags-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.log-row {
  margin-top: 10px;
}

.uniform-width {
  width: 100%;
}

.mt-4 {
  margin-top: 4px;
}

.el-button+.el-button {
  margin-left: 0 !important;
}

.button-jg {
  margin-left: 12px
}

.button-container {
  display: flex;
  gap: 12px;
}

.mt-8 {
  margin-top: 8px;
}

.ml-8 {
  margin-left: 8px;
}
</style>